import * as M from '@mantine/core';
import * as C from '../../../../../ui/common/CommonStyles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cleanParams } from '../../../../../../shared/utils/cleanParams';
import { modelFilterHook } from '../../../../../../shared/utils/modelFilter';
import { useSelectableTableCell } from '../../../../../../shared/hook/useSelectableTableCell';
import { IconSearch } from '@tabler/icons-react';
import type { Device } from '../../../../../../shared/types/device';
import { handleChangeLocaleTime } from '../../../../../../shared/utils/time';

const tableHead = [
  '번호',
  'MAC',
  '모드링크 모델',
  'SCAUTR 기계모델명',
  'PROGIX 기계장비명',
  '서비스 기간',
  '출고 상태',
  '메모',
  '상세 보기',
];

interface IPropsDeviceTable {
  modlinkInternalList: Device[];
  totalCount: number;
  checkBoxArr: string[];
  isAllChecked: boolean;
  onClickCheckBox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickCheckBoxAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function DeviceTable(props: IPropsDeviceTable) {
  const { modlinkInternalList, totalCount, checkBoxArr, isAllChecked, onClickCheckBox, onClickCheckBoxAll } = props;

  const navigate = useNavigate();
  const { tableWrapperRef, handleSelectCell, handleKeyDown, getSelectableCellStyle } = useSelectableTableCell<number>();
  const [searchParams, setSearchParams] = useSearchParams('');
  const page = searchParams.get('page') || '1';
  const size = searchParams.get('size') || '10';

  return (
    <>
      <M.Box
        ref={tableWrapperRef}
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => void handleKeyDown(e)}
        style={{ outline: 'none' }}
      >
        <M.Table.ScrollContainer minWidth={1000}>
          <M.Table withTableBorder captionSide="top" style={{ 'th, td': { textAlign: 'center' } }}>
            <M.Table.Thead>
              <M.Table.Tr>
                <M.Table.Th>
                  <M.Checkbox radius="xs" checked={isAllChecked} onChange={(e) => onClickCheckBoxAll(e)} />
                </M.Table.Th>
                {tableHead.map((item, index) => {
                  // eslint-disable-next-line react/no-array-index-key
                  return <M.Table.Th key={String(index)}>{item}</M.Table.Th>;
                })}
              </M.Table.Tr>
            </M.Table.Thead>
            <M.Table.Tbody>
              {modlinkInternalList.map((item) => {
                const serviceStartDate = item.serviceStartDt
                  ? handleChangeLocaleTime(item.serviceStartDt)?.substring(0, 10)
                  : '정보없음';
                const serviceEndDate = item.exp ? handleChangeLocaleTime(item.exp)?.substring(0, 10) : '정보없음';
                const servicePeriodText = `${serviceStartDate} ~ ${serviceEndDate}`;

                return (
                  <M.Table.Tr key={item.id}>
                    <M.Table.Td>
                      <M.Checkbox
                        id={String(item.mac)}
                        radius="xs"
                        disabled={item.shipment}
                        checked={checkBoxArr.some((mac) => mac === item.mac)}
                        onChange={(e) => onClickCheckBox(e)}
                      />
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'rowNumber')}
                      onClick={() => handleSelectCell(item.id, 'rowNumber', String(item.id))}
                    >
                      {item.id}
                      {/* {(data.count ?? 0) - ((page - 1) * 20 + (index ?? 0))} */}
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'mac')}
                      onClick={() => handleSelectCell(item.id, 'mac', item.mac)}
                    >
                      <M.Text
                        size="sm"
                        // style={{
                        //   textDecoration: 'underline',
                        //   cursor: 'pointer',
                        // }}
                        // onClick={() => navigate(`/admin/management/device/register/${item.id}`)}
                      >
                        {item.mac}
                      </M.Text>
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'deviceModel')}
                      onClick={() => handleSelectCell(item.id, 'deviceModel', modelFilterHook(item.deviceCd, item.deviceSubCd))}
                    >
                      {modelFilterHook(item.deviceCd, item.deviceSubCd)}
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'scautrModelName')}
                      onClick={() => handleSelectCell(item.id, 'scautrModelName', item.scautrModelName || '')}
                    >
                      {item.scautrModelName}
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'progixModelName')}
                      onClick={() => handleSelectCell(item.id, 'progixModelName', item.progixDeviceName || '')}
                    >
                      {item.progixDeviceName}
                    </M.Table.Td>

                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'servicePeriod')}
                      onClick={() => handleSelectCell(item.id, 'servicePeriod', servicePeriodText)}
                    >
                      {servicePeriodText}
                    </M.Table.Td>
                    <M.Table.Td
                      style={getSelectableCellStyle(item.id, 'shipmentStatus')}
                      onClick={() => handleSelectCell(item.id, 'shipmentStatus', item.shipment ? '출고 완료' : '출고 대기')}
                    >
                      {item.shipment ? '출고 완료' : '출고 대기'}
                    </M.Table.Td>
                    <M.Table.Td
                      style={{
                        ...getSelectableCellStyle(item.id, 'memo'),
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => handleSelectCell(item.id, 'memo', item.memo || '')}
                    >
                      {item.memo}
                    </M.Table.Td>
                    <M.Table.Td>
                      <M.Flex justify="center" align="center" height="100%">
                        <M.ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() => navigate(`/admin/management/device/details/${item.id}?mac=${item.mac}`)}
                        >
                          <IconSearch size={16} />
                        </M.ActionIcon>
                      </M.Flex>
                    </M.Table.Td>
                  </M.Table.Tr>
                );
              })}
            </M.Table.Tbody>
          </M.Table>
        </M.Table.ScrollContainer>
      </M.Box>
      <C.PaginationBox>
        <M.Pagination
          value={Number(page)}
          total={Math.ceil(totalCount / Number(size))}
          onChange={(value: number) => {
            const currentParams = Object.fromEntries(searchParams.entries());
            setSearchParams(
              cleanParams({
                ...currentParams,
                page: String(value),
              }),
            );
          }}
        />
      </C.PaginationBox>
    </>
  );
}
